/**
 * Size Selector Component
 * Handles size selection, updates dimensions display, and syncs with product variants
 */

if (!customElements.get('size-selector')) {
  class SizeSelector extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('.size-selector__button');
      this.currentSizeLabel = this.querySelector('[data-current-size]');
      this.largoValue = this.querySelector('[data-dimension="largo"]');
      this.anchoValue = this.querySelector('[data-dimension="ancho"]');
      this.altoValue = this.querySelector('[data-dimension="alto"]');
      
      // Get variant sync data
      this.sectionId = this.dataset.sectionId;
      this.optionName = this.dataset.optionName;
      this.optionIndex = parseInt(this.dataset.optionIndex);
    }

    connectedCallback() {
      this.buttons.forEach(button => {
        button.addEventListener('click', this.handleButtonClick.bind(this));
      });

      // Listen for variant changes from other sources (like the original variant picker)
      this.productPage = this.closest('[data-js-product-component]');
      if (this.productPage) {
        this.productVariants = this.productPage.querySelector('product-variants[data-main-product-variants]') 
          || this.productPage.querySelector('product-variants');
        if (this.productVariants) {
          this.productVariants.addEventListener('VARIANT_CHANGE', this.handleExternalVariantChange.bind(this));
        }
      }

      // Sync initial state
      this.syncWithVariantPicker();
    }

    handleButtonClick(event) {
      const button = event.currentTarget;
      
      // Check if unavailable
      if (button.hasAttribute('data-unavailable')) {
        return;
      }
      
      // Update active state
      this.buttons.forEach(btn => {
        btn.classList.remove('size-selector__button--active');
        btn.setAttribute('aria-pressed', 'false');
      });
      
      button.classList.add('size-selector__button--active');
      button.setAttribute('aria-pressed', 'true');

      // Update dimensions display
      this.updateDimensionsDisplay(button);

      // Sync with variant picker
      this.syncVariantPicker(button);
    }

    updateDimensionsDisplay(button) {
      const largo = button.dataset.largo || '—';
      const ancho = button.dataset.ancho || '—';
      const alto = button.dataset.alto || '—';
      const sizeName = button.textContent.trim();

      if (this.currentSizeLabel) {
        this.currentSizeLabel.textContent = sizeName;
      }
      
      if (this.largoValue) {
        this.largoValue.textContent = largo;
      }
      
      if (this.anchoValue) {
        this.anchoValue.textContent = ancho;
      }
      
      if (this.altoValue) {
        this.altoValue.textContent = alto;
      }
    }

    syncVariantPicker(button) {
      const sizeValue = button.dataset.sizeValue;
      const optionValueId = button.dataset.optionValueId;
      
      if (!this.productPage) return;

      let syncTriggered = false;

      // Method 1: Find variant picker by option name (e.g. product-medidas-123)
      if (this.optionName) {
        const variantPickerSelector = `[data-name="product-${this.optionName}-${this.sectionId}"]`;
        const variantPickerContainer = this.productPage.querySelector(variantPickerSelector);
        
        if (variantPickerContainer) {
          // Prefer option-value-id (most reliable, no escaping issues)
          let input = optionValueId 
            ? variantPickerContainer.querySelector(`input[data-option-value-id="${optionValueId}"]`)
            : null;
          
          if (!input && sizeValue) {
            const inputs = variantPickerContainer.querySelectorAll('input[value]');
            input = Array.from(inputs).find(inp => inp.value === sizeValue) || null;
          }
          
          if (!input) {
            const select = variantPickerContainer.querySelector('select');
            if (select) {
              let option = optionValueId 
                ? select.querySelector(`option[data-option-value-id="${optionValueId}"]`)
                : null;
              if (!option && sizeValue) {
                option = Array.from(select.options).find(opt => opt.value === sizeValue);
              }
              if (option) {
                select.value = option.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                syncTriggered = true;
              }
            }
          } else if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            syncTriggered = true;
          } else {
            syncTriggered = true;
          }
        }
      }

      // Method 2: Fallback - find by option-value-id anywhere in product-variants (price/variant sync)
      if (!syncTriggered && this.productVariants && optionValueId) {
        const targetInput = this.productVariants.querySelector(`input[data-option-value-id="${optionValueId}"]`);
        const targetOption = this.productVariants.querySelector(`option[data-option-value-id="${optionValueId}"]`);
        
        if (targetInput && !targetInput.checked) {
          targetInput.checked = true;
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          syncTriggered = true;
        } else if (targetOption) {
          const select = targetOption.closest('select');
          if (select && select.value !== targetOption.value) {
            select.value = targetOption.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            syncTriggered = true;
          }
        }
      }

      // Method 3: Direct variant update via fetch (when variant picker not in DOM but we have optionValueId)
      if (!syncTriggered && this.productVariants && optionValueId && !isNaN(this.optionIndex)) {
        this.triggerVariantUpdateByOption(optionValueId);
      }

      // Dispatch custom event for other integrations
      this.dispatchEvent(new CustomEvent('size:changed', {
        bubbles: true,
        detail: {
          sizeName: sizeValue,
          optionValueId: optionValueId,
          dimensions: {
            largo: button.dataset.largo,
            ancho: button.dataset.ancho,
            alto: button.dataset.alto
          }
        }
      }));
    }

    syncWithVariantPicker() {
      // Sync with current variant picker state on load
      if (!this.productPage || !this.optionName) return;

      const variantPickerSelector = `[data-name="product-${this.optionName}-${this.sectionId}"]`;
      const variantPickerContainer = this.productPage.querySelector(variantPickerSelector);
      
      if (variantPickerContainer) {
        const checkedInput = variantPickerContainer.querySelector('input:checked');
        const selectedSelect = variantPickerContainer.querySelector('select');
        
        let selectedValue = null;
        
        if (checkedInput) {
          selectedValue = checkedInput.value;
        } else if (selectedSelect) {
          selectedValue = selectedSelect.value;
        }
        
        if (selectedValue) {
          const matchingButton = this.querySelector(`.size-selector__button[data-size-value="${selectedValue}"]`);
          if (matchingButton && !matchingButton.classList.contains('size-selector__button--active')) {
            this.buttons.forEach(btn => {
              btn.classList.remove('size-selector__button--active');
              btn.setAttribute('aria-pressed', 'false');
            });
            matchingButton.classList.add('size-selector__button--active');
            matchingButton.setAttribute('aria-pressed', 'true');
            this.updateDimensionsDisplay(matchingButton);
          }
        }
      }
    }

    handleExternalVariantChange(event) {
      // When variant changes from another source, sync our buttons
      this.syncWithVariantPicker();
    }

    /**
     * Directly trigger variant update when variant picker inputs are not accessible.
     * Builds option hash with our size selection and fetches/render product updates (price, etc.).
     */
    triggerVariantUpdateByOption(optionValueId) {
      if (!this.productVariants || typeof this.productVariants.fetchProductUpdates !== 'function') return;

      const rows = this.productVariants.querySelectorAll('[data-js-product-variant-container]');
      const selections = [];
      let valid = true;

      rows.forEach((row, idx) => {
        if (idx === this.optionIndex) {
          selections.push(optionValueId);
        } else {
          let id = null;
          const checkedInput = row.querySelector('input:checked');
          const selectEl = row.querySelector('select');
          if (checkedInput) {
            id = checkedInput.dataset.optionValueId;
          } else if (selectEl?.selectedOptions?.[0]) {
            id = selectEl.selectedOptions[0].dataset.optionValueId;
          }
          if (id) {
            selections.push(id);
          } else {
            valid = false;
          }
        }
      });

      if (valid && selections.length === rows.length) {
        const optionHash = selections.join('-');
        this.productVariants.currentOptionValueHash = optionHash;
        this.productVariants.fetchProductUpdates(
          optionHash,
          this.productVariants.renderProductUpdates.bind(this.productVariants),
          true,
          false,
          'high'
        );
      }
    }
  }

  customElements.define('size-selector', SizeSelector);
}
